import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Skeleton, Alert, Box, Typography,
} from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { WorkflowHistoryRow } from './WorkflowHistoryRow';
import { getVisibleMetadata } from '../../workflowHistoryUtils';
import type { WorkflowHistoryItem } from '../../../../types/workflow/activity';

const headerCellSx = { py: 0.75, fontWeight: 600, fontSize: '0.75rem' } as const;

interface WorkflowHistoryTableProps {
  groupedHistory: Record<string, WorkflowHistoryItem[]>;
  loading: boolean;
  error: string | null;
  priorityFilter: string;
  totalCount: number;
}

export const WorkflowHistoryTable: React.FC<WorkflowHistoryTableProps> = ({
  groupedHistory,
  loading,
  error,
  priorityFilter,
  totalCount,
}) => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  if (loading) {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 40 }} />
              <TableCell sx={{ ...headerCellSx, width: 90 }}>Time</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 130 }}>Category</TableCell>
              <TableCell sx={headerCellSx}>Event</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 120 }}>User</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[0, 1, 2, 3].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={24} height={24} /></TableCell>
                <TableCell><Skeleton width={70} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={200} /></TableCell>
                <TableCell><Skeleton width={80} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {totalCount === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {priorityFilter === 'All'
              ? 'No history recorded yet.'
              : `No ${priorityFilter.toLowerCase()} priority events found.`}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: 40 }} />
                <TableCell sx={{ ...headerCellSx, width: 90 }}>Time</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 130 }}>Category</TableCell>
                <TableCell sx={headerCellSx}>Event</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 120 }}>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedHistory).map(([dateLabel, items]) => (
                <React.Fragment key={dateLabel}>
                  <TableRow>
                    <TableCell colSpan={5} sx={(theme) => ({ bgcolor: 'grey.100', py: 0.5, ...theme.applyStyles('dark', { bgcolor: 'grey.800' }) })}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {dateLabel}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {items.map((item) => {
                    const key = `${item.itemType}-${item.id}`;
                    const hasMetadata = getVisibleMetadata(item.metadata).length > 0;
                    return (
                      <WorkflowHistoryRow
                        key={key}
                        item={item}
                        expanded={expandedKey === key}
                        onToggle={() => setExpandedKey((prev) => prev === key ? null : key)}
                        hasMetadata={hasMetadata}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
